import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormField,
  Input,
} from "@/components/ui";
import { AUTHORITIES } from "@/config/constants";
import { useAppSelector } from "@/config/store";
import { hasAnyAuthority } from "@/shared/auth/private-route";
import { IEdoChat } from "@/shared/model/edo-chat.model";
import { IUser } from "@/shared/model/user.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { EntityState } from "@/shared/reducers/reducer.utils";
import axios from "axios";
import { SocketContext } from "context/socket";
import "moment/locale/it";
import { useContext, useEffect, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Translate, translate } from "react-jhipster";
import { z } from "zod";
import { Loader2, X } from "lucide-react";
import { cn } from "@/shared/util/cn";

const schema = z.object({
  title: z.string().min(1, "Required"),
  users: z.array(z.any()),
});

type FormValues = z.infer<typeof schema>;

export interface CreateGroupProps {
  open: boolean;
  handleClose: () => void;
  gruppo: IEdoChat | null;
}

const CreateGroupFormContent = ({
  utenteList,
  loadingUtenteList,
  utenteValue,
  setUtenteValue,
  searchUtente,
  account,
}: {
  utenteList: IUser[];
  loadingUtenteList: boolean;
  utenteValue: string;
  setUtenteValue: (v: string) => void;
  searchUtente: (q: string) => void;
  account: any;
}) => {
  const { control } = useFormContext<FormValues>();
  const [showResults, setShowResults] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (utenteValue.trim()) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => searchUtente(utenteValue), 300);
      setShowResults(true);
    } else {
      setShowResults(false);
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [utenteValue]);

  return (
    <div className="flex flex-col gap-4">
      <FormField name="title" label={translate("edocendoApp.edoChat.title")} required />
      <Controller
        name="users"
        control={control}
        render={({ field }) => {
          const selected = (field.value || []) as IUser[];
          const addUser = (u: IUser) => {
            if (selected.some((s) => s.id === u.id)) return;
            field.onChange([...selected, u]);
            setUtenteValue("");
            setShowResults(false);
          };
          const removeUser = (id: number) => {
            field.onChange(selected.filter((s) => s.id !== id));
          };
          return (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-[var(--color-mockup-text-mid)]">
                Utenti <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-2 rounded-2xl border border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 p-2 dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/30">
                {selected.map((u) => (
                  <Badge
                    key={u.id}
                    variant="secondary"
                    className="flex items-center gap-1 pr-1"
                  >
                    {u.firstName} {u.lastName}
                    <button
                      type="button"
                      onClick={() => removeUser(u.id)}
                      className="rounded-sm p-0.5 hover:bg-primary/15 dark:hover:bg-primary/20"
                      aria-label="Rimuovi"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                <div className="relative min-w-[140px] flex-1">
                  <Input
                    value={utenteValue}
                    onChange={(e) => setUtenteValue(e.target.value)}
                    onFocus={() => utenteValue && setShowResults(true)}
                    placeholder="Cerca utenti..."
                    className="h-8 border-0 bg-transparent shadow-none focus-visible:ring-2"
                  />
                  {showResults && (utenteValue || utenteList.length > 0) && (
                    <div className="absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-auto rounded-2xl border border-[var(--color-mockup-border)] bg-white shadow-lg dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]">
                      {loadingUtenteList ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        </div>
                      ) : utenteList.length === 0 ? (
                        <p className="px-3 py-2 text-body-sm text-[var(--color-mockup-text-light)]">
                          Nessun risultato
                        </p>
                      ) : (
                        utenteList
                          .filter((u) => !selected.some((s) => s.id === u.id))
                          .map((u) => (
                            <button
                              key={u.id}
                              type="button"
                              className={cn(
                                "w-full px-3 py-2 text-left text-body text-[var(--color-mockup-text)] hover:bg-[var(--color-surface-secondary)] dark:text-[var(--color-mockup-text)] dark:hover:bg-primary/15"
                              )}
                              onClick={() => addUser(u)}
                            >
                              {u.firstName} {u.lastName}
                            </button>
                          ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }}
      />
    </div>
  );
};

export const CreateGroup = (props: CreateGroupProps) => {
  const socket = useContext(SocketContext);
  const { loading, updating } = useAppSelector<EntityState<IEdoChat>>(
    (state) => state.edoChat
  );
  const { account } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );
  const [utenteList, setUtenteList] = useState<IUser[]>([]);
  const [utenteValue, setUtenteValue] = useState<string>("");
  const [loadingUtenteList, setLoadingUtenteList] = useState<boolean>(false);
  const { open, handleClose, gruppo } = props;

  const searchUtente = async (query: string) => {
    setLoadingUtenteList(true);
    const roleFilter = hasAnyAuthority(account?.roles || [], [
      AUTHORITIES.ROLE_STUDENT,
    ])
      ? { "filter.roles.name": "$eq:" + AUTHORITIES.ROLE_TEACHER }
      : {};
    const response = await axios.get("/users", {
      params: {
        page: 0,
        size: 5,
        sortBy: "firstName:ASC",
        search: query,
        "filter.id": "$not:" + account?.id,
        ...roleFilter,
      },
    });
    setUtenteList(response.data.data);
    setLoadingUtenteList(false);
  };

  const handleCloseDialog = () => {
    handleClose();
  };

  const createGroupChat = async (data: FormValues) => {
    const response = await axios.post(`/chats/group`, {
      title: data.title,
      users: data.users || [],
    });
    socket.emit("chat-created", response.data.chat);
    handleCloseDialog();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleCloseDialog()}>
      <DialogContent className="max-w-md sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Crea un gruppo</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <Form<FormValues>
            id="create-group-form"
            key={open ? "open" : "closed"}
            schema={schema}
            onSubmit={createGroupChat}
            defaultValues={{ title: gruppo?.title ?? "", users: [] }}
            className="flex grow flex-col gap-4"
          >
            <CreateGroupFormContent
              utenteList={utenteList}
              loadingUtenteList={loadingUtenteList}
              utenteValue={utenteValue}
              setUtenteValue={setUtenteValue}
              searchUtente={searchUtente}
              account={account}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                <Translate contentKey="entity.action.cancel" />
              </Button>
              <Button
                type="submit"
                form="create-group-form"
                disabled={updating}
              >
                {updating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Translate contentKey="entity.action.create" />
                )}
              </Button>
            </DialogFooter>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
